// PRE RMA — Nettoyage des données AVANT envoi au serveur (synchronisation).
// FONCTIONS PURES, partagées : le navigateur nettoie juste avant l'appel API, le serveur rejoue le même nettoyage
// (une saisie restée dans IndexedDB avant la correction repart donc elle aussi propre).
//
// Règles :
//  1. toute chaîne est « trimée » (espaces, retours à la ligne, espaces insécables) : clés de produit, tableau, N° RCE… ;
//  2. les champs de QUANTITÉ (reçu, utilisé, perdu, jours de rupture, montants, effectifs, doses…) vides / null / undefined
//     deviennent 0, avec Number(valeur) || 0 ;
//  3. aucune clé n'est AJOUTÉE : seules les clés déjà présentes sont nettoyées (le serveur refuse les « champs inconnus »,
//     par ex. sur les lignes de médicaments traceurs alimentées par l'inventaire) ;
//  4. EXCEPTION volontaire — champs où `null` veut dire « non saisi → calcul / report automatique » (stock début = SDU du mois
//     précédent, SDU = comptage du pré-inventaire, DTC1/DTC3 repris des séances, fin PEV calculée, PU du catalogue…).
//     Les forcer à 0 casserait ces reports : ils restent null (une chaîne vide devient null, une chaîne numérique devient un nombre).
//  5. Intrants PEV (tableau PEV_INTRANT) : normalisation complète (voir pevIntrants.ts) — identifiant en minuscules sans espace, reçu / périmé /
//     avarié / quantités par séance toujours présents et égaux à 0 quand ils sont vides, champs inconnus écartés.

import { normaliserCleIntrantPev, normaliserIntrantPev } from './pevIntrants.ts';

/** Champs de quantité : vide / null / undefined → 0. */
export const CHAMPS_QUANTITE = new Set([
  // Intrants hors PEV (T18)
  'recue', 'utilisee', 'redeployee', 'perdue', 'sortieAc', 'joursRupture',
  // Intrants PEV
  'recu', 'perime', 'avarie',
  // Inventaire
  'entree', 'depense',
  // Communication (T3), décès (T20)
  'hommes', 'femmes', 'nombre',
  // Finance (T19) : toutes les lignes de saisie (le solde début « b » est un report → voir CHAMPS_NULL_SIGNIFICATIF)
  'e1', 'e2', 'e3', 's1', 's2', 's3', 's4', 's5', 'r1', 'r2', 'r3', 'd1', 'd2', 'd3', 'cn', 'ca', 'bq', 'ph',
]);

/** Champs où null a un SENS (« non saisi » → report ou calcul automatique) : jamais forcés à 0. */
export const CHAMPS_NULL_SIGNIFICATIF = new Set([
  'debut', 'sdu', 'finSaisie', 'b', 'pu', 'detail', 'gros', 'dtc1', 'dtc3', 'pfa', 'rougeole', 'tnn',
]);

/** Objets dont TOUTES les valeurs sont des quantités (doses par antigène, quantités par séance). */
const CONTENEURS_QUANTITES = new Set(['doses', 'bySeance']);

/** Number(valeur) || 0, sans laisser passer Infinity. */
export const versNombre0 = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

/** trim() qui retire aussi les espaces insécables et les caractères de largeur nulle collés par un copier-coller. */
export const nettoyerTexte = (s: string): string => s.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ').trim();

const PROFONDEUR_MAX = 6;

function nettoyerValeur(v: unknown, cle: string | null, dansQuantites: boolean, profondeur: number): unknown {
  if (profondeur > PROFONDEUR_MAX) return v;

  if (dansQuantites || (cle !== null && CHAMPS_QUANTITE.has(cle))) {
    if (v !== null && typeof v === 'object') return v; // structure inattendue : on ne touche pas, le serveur validera
    if (typeof v === 'boolean') return v;
    return versNombre0(typeof v === 'string' ? nettoyerTexte(v) : v);
  }
  if (cle !== null && CHAMPS_NULL_SIGNIFICATIF.has(cle)) {
    if (v === null || v === undefined) return v;
    if (typeof v === 'string') {
      const t = nettoyerTexte(v);
      if (t === '') return null;
      const x = Number(t);
      return Number.isFinite(x) ? x : t; // texte non numérique : renvoyé tel quel, le serveur le refusera clairement
    }
    return v;
  }

  if (typeof v === 'string') return nettoyerTexte(v);
  if (Array.isArray(v)) return v.map((x) => nettoyerValeur(x, null, false, profondeur + 1)).filter((x) => x !== '');
  if (v !== null && typeof v === 'object') {
    const sortie: Record<string, unknown> = {};
    // Les valeurs d'un conteneur de quantités (doses, bySeance) sont toutes des quantités.
    const enfantsQuantites = cle !== null && CONTENEURS_QUANTITES.has(cle);
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      sortie[nettoyerTexte(k)] = nettoyerValeur(x, k, enfantsQuantites, profondeur + 1);
    }
    return sortie;
  }
  return v;
}

/** Nettoie un objet de saisie (donnees d'un module). Retourne une COPIE ; l'original n'est pas modifié. */
export function nettoyerDonnees<T>(donnees: T): T {
  return nettoyerValeur(donnees, null, false, 0) as T;
}

/**
 * Nettoie la saisie d'UNE ligne de module (clé + données), avec les règles propres au tableau.
 * Utilisé par le navigateur (avant l'envoi) ET par le serveur (à la réception) : le résultat est identique.
 */
export function nettoyerSaisieModule<T>(tableau: string, cle: string, donnees: T): { cle: string; donnees: T } {
  const propre = nettoyerDonnees(donnees);
  if (tableau === 'PEV_INTRANT') return { cle: normaliserCleIntrantPev(cle), donnees: normaliserIntrantPev(propre) as unknown as T };
  return { cle, donnees: propre };
}

/** Consultation RCE : trim uniquement. Les null (casFievre, tdrResultat, âge…) sont significatifs et restent null. */
export function nettoyerConsultation<T extends Record<string, unknown>>(payload: T): T {
  return nettoyerValeur(payload, null, false, 0) as T;
}

/** Saisie CPN : trim + N° de registre et âge (nombres) → Number(valeur) || 0 ; les indicateurs booléens ne changent pas. */
export function nettoyerCpn<T extends Record<string, unknown>>(payload: T): T {
  const p = nettoyerValeur(payload, null, false, 0) as Record<string, unknown>;
  for (const k of ['numeroRegistre', 'ageAns']) if (k in p) p[k] = versNombre0(p[k]);
  return p as T;
}

/** Une opération de la file « modules » (PEV, intrants, finance, décès, communication, inventaire, paramètres, catalogue). */
export function nettoyerOperationModule<T extends { type?: string }>(op: T): T {
  const o = { ...(op as Record<string, any>) };
  if (typeof o.tableau === 'string') o.tableau = nettoyerTexte(o.tableau);
  if (typeof o.cle === 'string') o.cle = nettoyerTexte(o.cle);
  if (typeof o.opId === 'string') o.opId = nettoyerTexte(o.opId);
  if (o.tableau === 'PEV_INTRANT' && typeof o.cle === 'string') o.cle = normaliserCleIntrantPev(o.cle); // UPSERT et DELETE : même identifiant que la base
  if (o.type === 'UPSERT' && o.donnees !== undefined) o.donnees = nettoyerSaisieModule(String(o.tableau ?? ''), String(o.cle ?? ''), o.donnees).donnees;
  if (o.type === 'PARAMETRES') {
    if (o.ajouterIntrant && typeof o.ajouterIntrant === 'object') o.ajouterIntrant = nettoyerDonnees(o.ajouterIntrant);
    if (typeof o.retirerIntrant === 'string') o.retirerIntrant = nettoyerTexte(o.retirerIntrant);
  }
  if (o.type === 'CATALOGUE' && Array.isArray(o.medicaments)) {
    o.medicaments = o.medicaments.map((m: any) => {
      const c = nettoyerDonnees(m) as Record<string, unknown>;
      if (c && typeof c === 'object' && 'pu' in c) c.pu = versNombre0(c.pu); // le prix unitaire est obligatoire dans le catalogue
      return c;
    });
  }
  return o as T;
}

/** Égalité profonde indépendante de l'ordre des clés (détecte une saisie « identique » déjà présente en base). */
export function memesDonnees(a: unknown, b: unknown): boolean {
  const canon = (x: unknown): unknown => Array.isArray(x) ? x.map(canon)
    : x !== null && typeof x === 'object' ? Object.fromEntries(Object.keys(x as object).sort().map((k) => [k, canon((x as any)[k])]))
    : x;
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b));
}
